import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, NativeSelect } from "@/components/common/Page";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { KINDS, METHODS, PAYMENT_STATUS, todayISO } from "@/lib/format";

export const StudentSelect = ({ value, onChange, open, testId }) => {
  const { data } = useFetch("/students", { skip: !open });
  return (
    <NativeSelect value={value || ""} onChange={onChange} required data-testid={testId}>
      <option value="">Selecione o aluno</option>
      {(data || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </NativeSelect>
  );
};

const useFormDialog = (open, initial) => {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setForm(initial);
      setError("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return { form, setForm, set, error, setError };
};

const save = async (fn, msg, onSaved, onOpenChange, setError) => {
  try {
    await fn();
    toast.success(msg);
    onSaved?.();
    onOpenChange(false);
  } catch (err) {
    setError(errMsg(err));
  }
};

export const PaymentFormDialog = ({ open, onOpenChange, payment, studentId, onSaved }) => {
  const initial = payment
    ? { ...payment, paid_date: payment.paid_date || "", method: payment.method || "" }
    : { student_id: studentId || "", description: "", kind: "avulso", amount: "", due_date: todayISO(), paid_date: "", method: "", status: "pendente" };
  const { form, set, error, setError } = useFormDialog(open, initial);

  const submit = (e) => {
    e.preventDefault();
    const body = { student_id: form.student_id, description: form.description, kind: form.kind, amount: Number(form.amount),
      due_date: form.due_date, paid_date: form.paid_date || null, method: form.method || null, status: form.status };
    save(() => (payment ? api.put(`/payments/${payment.id}`, body) : api.post("/payments", body)), "Pagamento salvo", onSaved, onOpenChange, setError);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="payment-form-dialog">
        <DialogHeader><DialogTitle>{payment ? "Editar pagamento" : "Novo pagamento"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Aluno *" className="sm:col-span-2"><StudentSelect open={open} value={form.student_id} onChange={set("student_id")} testId="payment-student-select" /></Field>
          <Field label="Descrição" className="sm:col-span-2"><Input value={form.description} onChange={set("description")} data-testid="payment-description-input" /></Field>
          <Field label="Tipo">
            <NativeSelect value={form.kind} onChange={set("kind")} data-testid="payment-kind-select">
              {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </NativeSelect>
          </Field>
          <Field label="Valor (R$) *"><Input type="number" min="0.01" step="0.01" value={form.amount} onChange={set("amount")} required data-testid="payment-amount-input" /></Field>
          <Field label="Vencimento *"><Input type="date" value={form.due_date} onChange={set("due_date")} required data-testid="payment-due-input" /></Field>
          <Field label="Status">
            <NativeSelect value={form.status} onChange={set("status")} data-testid="payment-status-select">
              {Object.entries(PAYMENT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </NativeSelect>
          </Field>
          {form.status === "pago" && (
            <>
              <Field label="Data do pagamento"><Input type="date" value={form.paid_date} onChange={set("paid_date")} data-testid="payment-paid-date-input" /></Field>
              <Field label="Forma de pagamento">
                <NativeSelect value={form.method} onChange={set("method")} data-testid="payment-method-select">
                  <option value="">Selecione</option>
                  {Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </NativeSelect>
              </Field>
            </>
          )}
          {error && <p className="text-sm text-destructive sm:col-span-2" data-testid="payment-form-error">{error}</p>}
          <DialogFooter className="sm:col-span-2"><Button type="submit" data-testid="payment-form-submit">Salvar</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const RecurringFormDialog = ({ open, onOpenChange, recurring, defaultDueDay = 10, onSaved }) => {
  const initial = recurring || { student_id: "", amount: "", due_day: defaultDueDay, description: "Mensalidade", active: true };
  const { form, set, error, setError } = useFormDialog(open, initial);

  const submit = (e) => {
    e.preventDefault();
    const body = { student_id: form.student_id, amount: Number(form.amount), due_day: Number(form.due_day), description: form.description, active: true };
    save(() => (recurring ? api.put(`/recurring/${recurring.id}`, body) : api.post("/recurring", body)), "Mensalidade salva", onSaved, onOpenChange, setError);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="recurring-form-dialog">
        <DialogHeader><DialogTitle>{recurring ? "Editar mensalidade" : "Nova mensalidade recorrente"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Aluno *" className="sm:col-span-2">
            {recurring ? <Input value={recurring.student_name} disabled /> : <StudentSelect open={open} value={form.student_id} onChange={set("student_id")} testId="recurring-student-select" />}
          </Field>
          <Field label="Valor mensal (R$) *"><Input type="number" min="1" step="0.01" value={form.amount} onChange={set("amount")} required data-testid="recurring-amount-input" /></Field>
          <Field label="Dia de vencimento *"><Input type="number" min="1" max="28" value={form.due_day} onChange={set("due_day")} required data-testid="recurring-due-day-input" /></Field>
          <Field label="Descrição" className="sm:col-span-2"><Input value={form.description} onChange={set("description")} data-testid="recurring-description-input" /></Field>
          <p className="text-xs text-muted-foreground sm:col-span-2">As cobranças são geradas automaticamente todo mês.</p>
          {error && <p className="text-sm text-destructive sm:col-span-2" data-testid="recurring-form-error">{error}</p>}
          <DialogFooter className="sm:col-span-2"><Button type="submit" data-testid="recurring-form-submit">Salvar</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PackageFormDialog = ({ open, onOpenChange, studentId, onSaved }) => {
  const { form, set, error, setError } = useFormDialog(open, { student_id: studentId || "", name: "Pacote 10 aulas", total_classes: 10, price: "", purchase_date: todayISO() });

  const submit = (e) => {
    e.preventDefault();
    const body = { ...form, total_classes: Number(form.total_classes), price: Number(form.price || 0), create_payment: true };
    save(() => api.post("/packages", body), "Pacote criado", onSaved, onOpenChange, setError);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="package-form-dialog">
        <DialogHeader><DialogTitle>Novo pacote de aulas</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Aluno *" className="sm:col-span-2"><StudentSelect open={open} value={form.student_id} onChange={set("student_id")} testId="package-student-select" /></Field>
          <Field label="Nome do pacote *" className="sm:col-span-2"><Input value={form.name} onChange={set("name")} required data-testid="package-name-input" /></Field>
          <Field label="Quantidade de aulas *"><Input type="number" min="1" value={form.total_classes} onChange={set("total_classes")} required data-testid="package-total-input" /></Field>
          <Field label="Valor (R$) *"><Input type="number" min="0" step="0.01" value={form.price} onChange={set("price")} required data-testid="package-price-input" /></Field>
          <Field label="Data da compra"><Input type="date" value={form.purchase_date} onChange={set("purchase_date")} data-testid="package-date-input" /></Field>
          <p className="text-xs text-muted-foreground sm:col-span-2">Uma cobrança pendente será criada e as próximas aulas do aluno serão vinculadas ao pacote.</p>
          {error && <p className="text-sm text-destructive sm:col-span-2" data-testid="package-form-error">{error}</p>}
          <DialogFooter className="sm:col-span-2"><Button type="submit" data-testid="package-form-submit">Criar pacote</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
